import React from 'react';
import {
  Avatar, Box, Stack, Typography, useColorScheme,
} from '@mui/joy';
import { RiBrainLine } from 'react-icons/ri';
import {
  FaAws, FaRobot, FaCode, FaBrain,
} from 'react-icons/fa';
import { Parallax } from 'react-scroll-parallax';
import { Default, Mobile, useMobileMode } from '../Responsive';

interface ExpertiseItem {
  id: string;
  title: string;
  description: string;
  icon: React.ReactElement;
  skills: string[];
}

const expertiseData: ExpertiseItem[] = [
  {
    id: 'ai-engineering',
    title: 'AI Engineering',
    description: 'Building intelligent systems that understand, learn, and adapt to user needs',
    icon: <FaBrain />,
    skills: ['LLM Integration', 'RAG Systems', 'Fine-tuning', 'Vector Databases'],
  },
  {
    id: 'chatbot-development',
    title: 'Chatbot Development',
    description: 'Creating conversational interfaces that provide natural, helpful interactions',
    icon: <FaRobot />,
    skills: ['Business Bots', 'Web Chat', 'Multi-modal', 'Context Management'],
  },
  {
    id: 'full-stack',
    title: 'Full Stack Development',
    description: 'Crafting end-to-end web applications with modern frameworks and tools',
    icon: <FaCode />,
    skills: ['React/Next.js', 'Node.js', 'Python', 'Database Design'],
  },
  {
    id: 'cloud-architecture',
    title: 'Cloud Architecture',
    description: 'Designing scalable, resilient systems on modern cloud platforms',
    icon: <FaAws />,
    skills: ['AWS', 'Docker', 'CI/CD', 'Microservices'],
  },
];

interface AnimatedCardProps {
  expertise: ExpertiseItem;
}

function AnimatedCard({ expertise }: AnimatedCardProps) {
  const mobile = useMobileMode();
  const { colorScheme } = useColorScheme();
  const dark = colorScheme === 'dark';

  return (
    <Box
      sx={(theme) => ({
        position: 'relative',
        cursor: 'pointer',
        borderRadius: '15px',
        padding: mobile ? '2rem 1rem 1rem 1rem' : '3.5rem 1rem 1rem 1rem',
        background: dark
          ? `color-mix(in srgb, ${theme.palette.background.body}, ${theme.palette.neutral.softBg} 15%)`
          : theme.palette.background.surface,
        border: `1px solid ${dark
          ? `rgba(${theme.vars.palette.neutral.mainChannel} / 0.1)`
          : `rgba(${theme.vars.palette.neutral.mainChannel} / 0.15)`
        }`,
        boxShadow: dark
          ? '0px 3px 6px rgba(0, 0, 0, 0.05), 0px 8px 15px rgba(0, 0, 0, 0.1)'
          : `0px 3px 6px rgba(${theme.vars.palette.neutral.mainChannel} / 0.04), 0px 2px 7px rgba(${theme.vars.palette.neutral.mainChannel} / 0.08)`,
        transition: 'all 0.25s ease',
        overflow: 'hidden',

        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: '15px',
          background: dark
            ? `rgba(${theme.vars.palette.neutral.mainChannel} / 0.015)`
            : 'transparent',
          zIndex: 0,
        },

        // Shine effect
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          background: dark
            ? `conic-gradient(from 205deg at 50% 50%, rgba(${theme.vars.palette.primary.mainChannel} / 0) 0deg, ${theme.palette.primary[500]} 25deg, rgba(${theme.vars.palette.primary.mainChannel} / 0.18) 295deg, rgba(${theme.vars.palette.primary.mainChannel} / 0) 360deg)`
            : `conic-gradient(from 225deg at 50% 50%, rgba(${theme.vars.palette.primary.mainChannel} / 0) 0deg, ${theme.palette.primary[500]} 25deg, ${theme.palette.primary.softBg} 285deg, ${theme.palette.background.surface} 345deg, rgba(${theme.vars.palette.primary.mainChannel} / 0) 360deg)`,
          opacity: 0,
          filter: 'blur(35px)',
          transform: 'scale(1.5)',
          transition: 'opacity 0.5s ease',
          zIndex: 1,
        },

        '&:hover': {
          transform: mobile ? 'none' : 'translateY(-0.6rem)',
          filter: mobile ? 'none' : `drop-shadow(0 0.6rem 0 ${theme.palette.primary.solidBg})`,
          borderColor: dark
            ? `rgba(${theme.vars.palette.neutral.mainChannel} / 0.2)`
            : `rgba(${theme.vars.palette.neutral.mainChannel} / 0.25)`,
          boxShadow: dark
            ? '0px 5px 10px rgba(0, 0, 0, 0.04), 0px 15px 25px rgba(0, 0, 0, 0.3)'
            : `0px 5px 10px rgba(${theme.vars.palette.neutral.mainChannel} / 0.06), 0px 15px 25px rgba(${theme.vars.palette.neutral.mainChannel} / 0.12)`,
          zIndex: 1,

          '&::after': {
            opacity: dark ? 0.1 : 0.3,
          },

          '& .expertise-icon': {
            backgroundColor: `rgba(${theme.vars.palette.primary.mainChannel} / 0.1)`,
            borderColor: `rgba(${theme.vars.palette.primary.mainChannel} / 0.2)`,
            color: dark ? theme.palette.primary.solidColor : theme.palette.primary.solidBg,
            transform: 'scale(1.1)',
          },

          '& .background-pattern': {
            opacity: 1,
            '& .tile': {
              animationName: 'tileAnimation',
            },
          },

          '& .grid-lines': {
            opacity: 1,
            '& .line::before': {
              transform: 'scaleX(1)',
            },
            '& .line::after': {
              transform: 'scaleY(1)',
            },
          },
        },
      })}
    >
      {/* Background Pattern */}
      <Box
        className="background-pattern"
        sx={() => ({
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          mask: 'radial-gradient(circle at 60% 5%, black 0%, black 15%, transparent 60%)',
          WebkitMask: 'radial-gradient(circle at 60% 5%, black 0%, black 15%, transparent 60%)',
          opacity: 0,
          transition: 'opacity 0.25s ease 0.25s',
          zIndex: 1,
        })}
      >
        {/* Animated Tiles */}
        {Array.from({ length: 10 }, (_, i) => i).map((i) => (
          <Box
            key={`tile-${expertise.id}-${i}`}
            className="tile"
            sx={(theme) => ({
              position: 'absolute',
              background: `rgba(${theme.vars.palette.primary.mainChannel} / 0.05)`,
              opacity: 0,
              '@keyframes tileAnimation': {
                '0%, 12.5%, 100%': { opacity: 1 },
                '25%, 82.5%': { opacity: 0 },
              },
              animationDuration: '8s',
              animationIterationCount: 'infinite',
              animationDelay: (() => {
                if (i % 3 === 0) return '-2s';
                if (i % 2 === 0) return '-4s';
                return '-6s';
              })(),

              // Tile positioning (simplified grid)
              ...(i === 0 && {
                top: 0, left: 0, height: '10%', width: '22.5%',
              }),
              ...(i === 1 && {
                top: 0, left: '22.5%', height: '10%', width: '27.5%',
              }),
              ...(i === 2 && {
                top: 0, left: '50%', height: '10%', width: '27.5%',
              }),
              ...(i === 3 && {
                top: 0, left: '77.5%', height: '10%', width: '22.5%',
              }),
              ...(i === 4 && {
                top: '10%', left: 0, height: '22.5%', width: '22.5%',
              }),
              ...(i === 5 && {
                top: '10%', left: '22.5%', height: '22.5%', width: '27.5%',
              }),
              ...(i === 6 && {
                top: '10%', left: '50%', height: '22.5%', width: '27.5%',
              }),
              ...(i === 7 && {
                top: '10%', left: '77.5%', height: '22.5%', width: '22.5%',
              }),
              ...(i === 8 && {
                top: '32.5%', left: '50%', height: '22.5%', width: '27.5%',
              }),
              ...(i === 9 && {
                top: '32.5%', left: '77.5%', height: '22.5%', width: '22.5%',
              }),
            })}
          />
        ))}
      </Box>

      {/* Grid Lines */}
      <Box
        className="grid-lines"
        sx={() => ({
          position: 'absolute',
          inset: 0,
          opacity: 0,
          transition: 'opacity 0.35s ease',
          zIndex: 1,
        })}
      >
        {[0, 1, 2, 3].map((lineIndex) => (
          <Box
            key={lineIndex}
            className="line"
            sx={(theme) => ({
              position: 'absolute',
              inset: 0,
              '&::before, &::after': {
                content: '""',
                position: 'absolute',
                backgroundColor: dark
                  ? theme.palette.neutral[800]
                  : theme.palette.neutral[200],
                transition: 'transform 0.35s ease',
                transitionDelay: `${(3 - lineIndex) * 0.15}s`,
              },
              '&::before': {
                left: 0,
                right: 0,
                height: '1px',
                transform: 'scaleX(0)',
                transformOrigin: '0 50%',
                top: `${lineIndex === 0 ? 10 : lineIndex * 22.5 + 10}%`,
              },
              '&::after': {
                top: 0,
                bottom: 0,
                width: '1px',
                transform: 'scaleY(0)',
                transformOrigin: '50% 0',
                left: (() => {
                  if (lineIndex === 0) return '22.5%';
                  if (lineIndex === 1) return '50%';
                  if (lineIndex === 2) return '77.5%';
                  return '77.5%';
                })(),
              },
            })}
          />
        ))}
      </Box>

      {/* Icon */}
      <Avatar
        className="expertise-icon"
        color="neutral"
        variant="outlined"
        sx={(theme) => ({
          position: 'relative',
          zIndex: 2,
          width: '40px',
          height: '40px',
          backgroundColor: dark
            ? `rgba(${theme.vars.palette.neutral.mainChannel} / 0.08)`
            : `rgba(${theme.vars.palette.neutral.mainChannel} / 0.04)`,
          borderColor: dark
            ? `rgba(${theme.vars.palette.neutral.mainChannel} / 0.12)`
            : `rgba(${theme.vars.palette.neutral.mainChannel} / 0.1)`,
          backdropFilter: 'blur(2px)',
          transition: 'all 0.25s ease',
          fontSize: '1.2rem',
        })}
      >
        {expertise.icon}
      </Avatar>

      {/* Title */}
      <Typography
        level="h4"
        sx={(theme) => ({
          position: 'relative',
          zIndex: 2,
          marginTop: '12px',
          marginBottom: '4px',
          fontWeight: 600,
          fontSize: '0.875rem',
          lineHeight: 2,
          color: dark ? theme.palette.common.white : theme.palette.neutral[900],
        })}
      >
        {expertise.title}
      </Typography>

      {/* Description */}
      <Typography
        level="body2"
        sx={(theme) => ({
          position: 'relative',
          zIndex: 2,
          fontSize: '0.875rem',
          lineHeight: 1.7,
          color: theme.palette.text.secondary,
          marginBottom: '1rem',
        })}
      >
        {expertise.description}
      </Typography>

      {/* Skills */}
      <Stack
        gap={0.5}
        sx={{
          position: 'relative',
          zIndex: 2,
        }}
      >
        {expertise.skills.map((skill) => (
          <Typography
            key={skill}
            level="body2"
            sx={(theme) => ({
              fontSize: '0.8rem',
              color: theme.palette.text.secondary,
              display: 'flex',
              alignItems: 'center',
              '&::before': {
                content: '"•"',
                color: dark ? theme.palette.primary.solidColor : theme.palette.primary.solidBg,
                fontWeight: 'bold',
                marginRight: '8px',
                fontSize: '1rem',
              },
            })}
          >
            {skill}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

export default function Expertise() {
  const mobile = useMobileMode();

  return (
    <Stack
      gap={3}
      alignItems={mobile ? 'center' : 'flex-start'}
      sx={(theme) => ({
        position: 'relative',
        '&::before': mobile ? {} : {
          content: '""',
          position: 'absolute',
          top: '-80px',
          left: '-50px',
          height: 'calc(100% + 160px)',
          width: '2px',
          background: `linear-gradient(to bottom, ${theme.palette.primary[500]}, ${theme.palette.primary[500]})`,
        },
      })}
    >
      <Mobile>
        <Avatar
          color="primary"
          sx={(theme) => ({
            position: 'relative',
            border: 'none',
            outline: `2px solid ${theme.palette.primary[500]}`,
            boxShadow: `0 0 40px 5px rgba(${theme.palette.primary.mainChannel} / 0.4)`,
            overflow: 'visible',
            marginTop: '3rem',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-5rem',
              height: '5rem',
              width: '2px',
              background: `linear-gradient(to bottom, transparent, ${theme.palette.primary[500]})`,
            },
          })}
        >
          <RiBrainLine />
        </Avatar>
      </Mobile>

      <Typography
        level="h2"
        sx={{ position: 'relative', textAlign: mobile ? 'center' : 'left' }}
        id="expertise"
      >
        <Default>
          <Avatar
            color="primary"
            sx={(theme) => ({
              position: 'absolute',
              left: '-49px',
              top: '0',
              transform: 'translateX(-50%)',
              border: 'none',
              outline: `2px solid ${theme.palette.primary[500]}`,
              boxShadow: `0 0 40px 5px rgba(${theme.palette.primary.mainChannel} / 0.4)`,
            })}
          >
            <RiBrainLine />
          </Avatar>
        </Default>

        Technical
        {' '}
        <Typography
          color="primary"
          alignItems="center"
          fontWeight="xl"
        >
          Expertise
        </Typography>
      </Typography>

      <Parallax
        opacity={[0, 1]}
        disabled={mobile}
        easing="easeOutBack"
        style={{ width: '100%' }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: mobile ? '1fr' : 'repeat(4, 1fr)',
            gap: mobile ? '2rem' : '2rem',
            width: '100%',
            maxWidth: mobile ? '100%' : '100%',
          }}
        >
          {expertiseData.map((expertise) => (
            <AnimatedCard
              key={expertise.id}
              expertise={expertise}
            />
          ))}
        </Box>
      </Parallax>
    </Stack>
  );
}
